import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  currentPosition: string | null;
  currentCompany: string | null;
  status: string;
  requisition: {
    jobRole: { roleName: string } | null;
  } | null;
}

const getStatusStyle = (status: string) => {
  switch (status) {
    case "SHORTLISTED":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "PHONE_SCREEN":
      return "bg-indigo-50 text-indigo-700 border-indigo-100";
    case "INTERVIEW":
      return "bg-purple-50 text-purple-700 border-purple-100";
    case "PRE_EMPLOYMENT_COMMENCED":
      return "bg-teal-50 text-teal-700 border-teal-100";
    case "PRE_EMPLOYMENT_COMPLETED":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "DRAFT_OFFER":
    case "OFFER_SENT":
      return "bg-orange-50 text-orange-700 border-orange-100";
    case "OFFER_ACCEPTED":
      return "bg-green-50 text-green-700 border-green-100";
    case "OFFER_REJECTED":
    case "REJECTED":
      return "bg-red-50 text-red-700 border-red-100";
    case "OFFER_EXPIRED":
      return "bg-gray-50 text-gray-700 border-gray-100";
    case "HIRING_COMMENCED":
      return "bg-slate-900 text-white";
    case "HIRED":
      return "bg-green-600 text-white";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "SHORTLISTED": return "Shortlisted";
    case "PHONE_SCREEN": return "Phone Screen";
    case "INTERVIEW": return "Interview";
    case "PRE_EMPLOYMENT_COMMENCED": return "Pre-Employment";
    case "PRE_EMPLOYMENT_COMPLETED": return "Pre-Employment Done";
    case "DRAFT_OFFER": return "Draft Offer";
    case "OFFER_SENT": return "Offer Sent";
    case "OFFER_ACCEPTED": return "Offer Accepted";
    case "OFFER_REJECTED": return "Offer Rejected";
    case "OFFER_EXPIRED": return "Offer Expired";
    case "REJECTED": return "Rejected";
    case "HIRING_COMMENCED": return "Hiring Commenced";
    case "HIRED": return "Hired";
    default: return status;
  }
};

const CandidatesList: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_LIST) && !ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE)) {
    return <AccessRestricted />;
  }
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const res = await api.get<{ success: boolean; candidates: Candidate[] }>(
          "/api/candidates?limit=50"
        );
        if (res.success) {
          setCandidates(res.candidates);
        }
      } catch (error) {
        console.error("Failed to fetch candidates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, []);

  const filteredCandidates = useMemo(() => {
    if (searchQuery === "") return candidates;
    const q = searchQuery.toLowerCase();
    return candidates.filter((candidate) => {
      const name = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
      const role = (candidate.currentPosition || "").toLowerCase();
      const company = (candidate.currentCompany || "").toLowerCase();
      const appliedFor = (candidate.requisition?.jobRole?.roleName || "").toLowerCase();
      const status = candidate.status.toLowerCase();
      return (
        name.includes(q) ||
        role.includes(q) ||
        company.includes(q) ||
        appliedFor.includes(q) ||
        status.includes(q)
      );
    });
  }, [searchQuery, candidates]);

  return (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-bold text-gray-900 text-lg">All Candidates</h4>
          <p className="text-gray-500 text-sm font-medium">
            Candidates across all recruitment requisitions
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <Input
            placeholder="Search candidates..."
            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Candidate Cards */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <p className="text-sm font-medium">
              {searchQuery ? "No candidates match your search" : "No candidates yet"}
            </p>
          </div>
        ) : (
          filteredCandidates.map((candidate) => (
            <div
              key={candidate.id}
              className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900">
                  {candidate.firstName} {candidate.lastName}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  {candidate.currentPosition
                    ? `${candidate.currentPosition}${candidate.currentCompany ? ` at ${candidate.currentCompany}` : ""}`
                    : "—"}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  Applied for: {candidate.requisition?.jobRole?.roleName || "—"}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <Badge
                  variant="outline"
                  className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusStyle(
                    candidate.status
                  )}`}
                >
                  {formatStatusLabel(candidate.status)}
                </Badge>
                <button
                  onClick={() =>
                    navigate(`/recruitment/candidates/${candidate.id}`)
                  }
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CandidatesList;
